import React from "react";

/**
 * Inline SVGs for the Bulk Send → Signers step, copied path-for-path from
 * `Bulk Send.dc.html` so stroke widths and geometry match the design exactly.
 * Every icon inherits `currentColor`; the parent sets the colour.
 */

interface IconProps {
  size?: number;
}

const base = (
  size: number,
  strokeWidth: number,
  children: React.ReactNode,
  extra?: React.SVGProps<SVGSVGElement>,
) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    style={{ width: size, height: size, flexShrink: 0, pointerEvents: "none" }}
    {...extra}
  >
    {children}
  </svg>
);

export const DownloadIcon = ({ size = 15 }: IconProps) =>
  base(
    size,
    2,
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M7 10l5 5 5-5" />
      <path d="M12 15V3" />
    </>,
  );

export const AlertCircleIcon = ({ size = 12 }: IconProps) =>
  base(
    size,
    2,
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5" />
      <path d="M12 16.5h.01" />
    </>,
  );

export const CheckIcon = ({ size = 12 }: IconProps) =>
  base(size, 2.5, <path d="M5 13l4 4L19 7" />);

export const TrashSimpleIcon = ({ size = 12 }: IconProps) =>
  base(
    size,
    2,
    <>
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </>,
  );

export const TrashIcon = ({ size = 15 }: IconProps) =>
  base(
    size,
    2,
    <>
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </>,
  );

export const FileLinesIcon = ({ size = 14 }: IconProps) =>
  base(
    size,
    2,
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <path d="M8 13h8" />
      <path d="M8 17h5" />
    </>,
  );

export const FileIcon = ({ size = 13 }: IconProps) =>
  base(
    size,
    2,
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
    </>,
  );

export const PlusIcon = ({ size = 13 }: IconProps) =>
  base(
    size,
    2.5,
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </>,
  );

export const CloseIcon = ({ size = 11 }: IconProps) =>
  base(
    size,
    2,
    <>
      <path d="M18 6L6 18" />
      <path d="M6 6l12 12" />
    </>,
  );

export const ArrowLeftIcon = ({ size = 13 }: IconProps) =>
  base(
    size,
    2.5,
    <>
      <path d="M19 12H5" />
      <path d="M11 18l-6-6 6-6" />
    </>,
  );

export const ArrowRightIcon = ({ size = 16 }: IconProps) =>
  base(
    size,
    2,
    <>
      <path d="M5 12h14" />
      <path d="M13 6l6 6-6 6" />
    </>,
  );

export const FileUploadIcon = ({ size = 44 }: IconProps) =>
  base(
    size,
    1.6,
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <path d="M9.5 14.5l2.5-2.5 2.5 2.5" />
      <path d="M12 12v6" />
    </>,
  );

export const InfoIcon = ({ size = 15 }: IconProps) =>
  base(
    size,
    2,
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8h.01" />
      <path d="M12 11v5" />
    </>,
    { strokeLinecap: undefined },
  );

export const SparkleIcon = ({ size = 16 }: IconProps) =>
  base(
    size,
    2,
    <>
      <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
      <path d="M19 15l.9 2.6L22.5 18.5l-2.6.9L19 22l-.9-2.6-2.6-.9 2.6-.9z" />
    </>,
  );

export const SparkleSmallIcon = ({ size = 10 }: IconProps) =>
  base(
    size,
    2,
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />,
  );

export const ChevronDownIcon = ({ size = 10 }: IconProps) =>
  base(size, 2.5, <path d="M6 9l6 6 6-6" />);

export const CloudUploadIcon = ({ size = 44 }: IconProps) =>
  base(
    size,
    1.6,
    <>
      <path d="M17.5 18a4.5 4.5 0 0 0 .9-8.9A7 7 0 0 0 5 11.5 3.75 3.75 0 0 0 6.5 18z" />
      <path d="M9.5 13.5l2.5-2.5 2.5 2.5" />
      <path d="M12 11v6" />
    </>,
  );

export const CloudIcon = ({ size = 17 }: IconProps) =>
  base(
    size,
    2,
    <path d="M17.5 19a4.5 4.5 0 0 0 .9-8.9A7 7 0 0 0 5 12.5 3.75 3.75 0 0 0 6.5 19z" />,
  );

export const DragDotsIcon = ({ size = 12 }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ width: size, height: size, flexShrink: 0, pointerEvents: "none" }}
  >
    <circle cx="9" cy="5" r="1.6" />
    <circle cx="15" cy="5" r="1.6" />
    <circle cx="9" cy="12" r="1.6" />
    <circle cx="15" cy="12" r="1.6" />
    <circle cx="9" cy="19" r="1.6" />
    <circle cx="15" cy="19" r="1.6" />
  </svg>
);
